import { createSlice } from '@reduxjs/toolkit'

import type { RootState } from '../index'
import { selectorHookFactory } from '../utils'

/**
 * ⚠️  PERSISTED SLICE — see the safety notes in `tenant.ts` before changing its shape. ⚠️
 *
 * Demo mode anonymizes customer-identifying data across the app for live customer demos
 * (see `src/features/demoMode/`). The flag is deliberately persisted to localStorage so a
 * mid-demo tab refresh comes back still anonymized — a refresh that flashed real customer
 * names or site locations during a demo would defeat the entire feature. Only add optional
 * fields here, never rename or re-type existing ones.
 *
 * This slice deliberately exposes NO actions: the only state change is redux-persist
 * rehydration on page load. Toggling demo mode must go through `useSetDemoMode`
 * (src/features/demoMode/useSetDemoMode.ts), which writes the persisted flag directly to
 * storage and reloads — flipping the flag on a live tree would re-key DataManager's
 * IndexedDB cache mid-session and could write real PII into the demo cache.
 */

type DemoModeState = {
  enabled?: boolean
}

const initialState: DemoModeState = {
  enabled: false
}

const demoModeSlice = createSlice({
  name: 'demoMode',
  initialState,
  reducers: {}
})

export default demoModeSlice.reducer

const selectIsDemoMode = (state: RootState) => state.demoMode?.enabled ?? false

export const useIsDemoMode = selectorHookFactory(selectIsDemoMode)
